/**
 * Conditional display rules for dynamic intake form fields.
 *
 * `intake_form_fields.conditional_display` is served to the client by
 * `api/repositories/intakeFormRepository.js:47` as `conditionalDisplay`.
 * Two rule shapes exist in the wild:
 *
 *   SELF-GATING (used by TRT / DoctorNow parity work):
 *     { type: 'show', when: { field: '<other field_id>', value: 'Yes' } }
 *     The rule lives on the field it gates. This mirrors the DoctorNow
 *     vendor schema's `depends_on: { field, show_if }`.
 *
 *   LEGACY TRIGGER-SIDE (7 rows live, 2 forms, measured 2026-08-14):
 *     { type: 'show', when: { field: 'q5', value: 'yes' }, target: 'q5a' }
 *     The rule lives on the TRIGGER and names a foreign `target` by a short
 *     id (`q5a`) that matches no real `field_id`. These are deliberately
 *     IGNORED — they gate nothing today, so ignoring them is a provable
 *     no-op. Resolving them needs a data migration that maps `qNa` -> field_id.
 */

const normalizeScalar = (value) => String(value ?? '').trim().toLowerCase();

/**
 * Flatten any answer shape into the list of scalar values it represents.
 * Handles plain scalars, checkbox/multiselect arrays, and the two
 * `allow_other` object shapes IntakePage stores ({ selected } / { selections }).
 */
export const toAnswerValues = (answer) => {
  if (answer === null || answer === undefined) return [];
  if (Array.isArray(answer)) return answer.map(normalizeScalar).filter(Boolean);
  if (typeof answer === 'object') {
    const { selected, selections, other } = answer;
    return [
      ...(Array.isArray(selections) ? selections : []),
      ...(selected === undefined ? [] : [selected]),
      ...(other ? [other] : [])
    ].map(normalizeScalar).filter(Boolean);
  }
  const scalar = normalizeScalar(answer);
  return scalar ? [scalar] : [];
};

/**
 * Does a rule's `when` clause match the current answers?
 *
 * Comparison is case-insensitive on trimmed strings by design: DoctorNow
 * ships `"Yes"` while Oval's legacy options ship `"yes"`, and a case-sensitive
 * compare would silently never match — which renders as a field that can
 * never appear. This normalizes only the COMPARISON; stored option values are
 * untouched and stay byte-identical to the vendor schema.
 */
export const isConditionMet = (when, answers) => {
  if (!when || !when.field) return false;
  const expected = [
    ...(Array.isArray(when.values) ? when.values : []),
    ...(when.value === undefined ? [] : [when.value])
  ].map(normalizeScalar);
  if (expected.length === 0) return false;
  const actual = toAnswerValues((answers || {})[when.field]);
  return actual.some((value) => expected.includes(value));
};

/** Rules that gate the field they are stored on (see module header). */
const getSelfGatingRules = (question) => (question?.conditionalDisplay || [])
  .filter((rule) => rule && rule.when && (rule.target == null || rule.target === question.id));

/**
 * A question with no self-gating rules is always visible (fail-open — an
 * unrecognized or unresolvable rule must never hide a question that renders today).
 */
export const isQuestionVisible = (question, answers) => {
  const rules = getSelfGatingRules(question);
  if (rules.length === 0) return true;
  const shows = rules.filter((rule) => rule.type !== 'hide');
  const hides = rules.filter((rule) => rule.type === 'hide');
  if (hides.some((rule) => isConditionMet(rule.when, answers))) return false;
  if (shows.length === 0) return true;
  return shows.some((rule) => isConditionMet(rule.when, answers));
};

export const filterVisibleQuestions = (questions, answers) =>
  (questions || []).filter((question) => isQuestionVisible(question, answers));

/** Answers belonging to questions that are currently hidden must not be submitted. */
export const omitHiddenAnswers = (questions, answers) => {
  const visibleIds = new Set(filterVisibleQuestions(questions, answers).map((q) => q.id));
  const conditionalIds = (questions || [])
    .filter((q) => getSelfGatingRules(q).length > 0)
    .map((q) => q.id);
  return Object.fromEntries(
    Object.entries(answers || {})
      .filter(([id]) => !conditionalIds.includes(id) || visibleIds.has(id))
  );
};
